import { useState } from 'react'
import { Button, Empty, Input, InputNumber, Space, Table, Typography } from '@arco-design/web-react'
import { IconCopy, IconDelete, IconPlus } from '@arco-design/web-react/icon'
import { Pill } from '@/utils/render'
import type { GroupRow } from '../ratioSettingUtils'
import { createRatioRowId } from '../ratioSettingUtils'

interface Props {
  rows: GroupRow[]
  onChange: (rows: GroupRow[]) => void
  /** AutoGroups 里的分组 key，用于标记“auto 会尝试这个分组” */
  autoGroupKeys?: string[]
}

export default function GroupRatioEditor({ rows, onChange, autoGroupKeys = [] }: Props) {
  const [bulkRatio, setBulkRatio] = useState<number>(1)

  const updateRow = (id: string, patch: Partial<GroupRow>) => {
    onChange(rows.map((row) => (row.id === id ? { ...row, ...patch } : row)))
  }

  // 重复 key 会让 GroupRatio JSON 里后者覆盖前者，必须提前提示
  const keyCounts = rows.reduce<Record<string, number>>((acc, row) => {
    const key = row.key.trim()
    if (key) acc[key] = (acc[key] ?? 0) + 1
    return acc
  }, {})

  const duplicateKeys = Object.keys(keyCounts).filter((k) => keyCounts[k] > 1)

  const applyBulkRatio = () => {
    onChange(rows.map((row) => ({ ...row, ratio: bulkRatio })))
  }

  const copyRow = (row: GroupRow) => {
    const base = row.key.trim() || 'group'
    let suffix = 1
    let nextKey = `${base}_copy`
    while (rows.some((item) => item.key.trim() === nextKey)) {
      suffix += 1
      nextKey = `${base}_copy${suffix}`
    }
    onChange([
      ...rows,
      {
        id: createRatioRowId('group'),
        key: nextKey,
        displayName: row.displayName ? `${row.displayName} 副本` : '',
        ratio: row.ratio,
      },
    ])
  }

  const columns = [
    {
      title: '分组 Key',
      dataIndex: 'key',
      render: (_: unknown, row: GroupRow) => {
        const duplicated = !!row.key.trim() && keyCounts[row.key.trim()] > 1
        return (
          <div className="space-y-1">
            <Input
              placeholder="default / vip / svip"
              value={row.key}
              status={duplicated ? 'error' : undefined}
              onChange={(value) => updateRow(row.id, { key: value })}
            />
            <div className="flex flex-wrap gap-1">
              {duplicated ? <Pill variant="danger">Key 重复</Pill> : null}
              {autoGroupKeys.includes(row.key.trim()) ? (
                <Pill variant="info">auto 候选</Pill>
              ) : null}
            </div>
          </div>
        )
      },
    },
    {
      title: '展示名',
      dataIndex: 'displayName',
      render: (_: unknown, row: GroupRow) => (
        <Input
          placeholder="默认分组 / VIP 分组"
          value={row.displayName}
          onChange={(value) => updateRow(row.id, { displayName: value })}
        />
      ),
    },
    {
      title: '用户可见',
      width: 120,
      render: (_: unknown, row: GroupRow) =>
        row.displayName.trim() ? (
          <Pill variant="success">可见</Pill>
        ) : (
          <Pill variant="neutral">仅路由/计费</Pill>
        ),
    },
    {
      title: '基础倍率',
      dataIndex: 'ratio',
      width: 180,
      render: (_: unknown, row: GroupRow) => (
        <InputNumber
          min={0}
          step={0.1}
          className="!w-full"
          value={row.ratio}
          onChange={(value) =>
            updateRow(row.id, { ratio: value == null || !Number.isFinite(value) ? 0 : value })
          }
        />
      ),
    },
    {
      title: '操作',
      width: 90,
      render: (_: unknown, row: GroupRow) => (
        <Space size={4}>
          <Button
            type="text"
            icon={<IconCopy />}
            aria-label="复制分组"
            onClick={() => copyRow(row)}
          />
          <Button
            type="text"
            status="danger"
            icon={<IconDelete />}
            aria-label="删除分组"
            onClick={() => onChange(rows.filter((item) => item.id !== row.id))}
          />
        </Space>
      ),
    },
  ]

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <Typography.Text bold>分组倍率与展示名</Typography.Text>
          <Typography.Paragraph type="secondary" className="!text-xs !mb-0">
            分组 Key 写入 GroupRatio，用于渠道路由与基础计费倍率；展示名写入
            UserUsableGroups，仅决定普通用户可见/可选时的文案。
            展示名为空时，该分组仍可作为渠道分组计费，但不会自动出现在用户可用分组里。
          </Typography.Paragraph>
        </div>
        <Button
          type="outline"
          icon={<IconPlus />}
          onClick={() =>
            onChange([
              ...rows,
              { id: createRatioRowId('group'), key: '', displayName: '', ratio: 1 },
            ])
          }
        >
          添加分组
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Space>
          <span className="text-xs text-[var(--console-text-muted)]">统一设置倍率</span>
          <InputNumber
            min={0}
            step={0.1}
            value={bulkRatio}
            onChange={(v) => setBulkRatio(v == null || !Number.isFinite(v) ? 1 : v)}
            style={{ width: 110 }}
          />
          <Button size="mini" onClick={applyBulkRatio}>
            应用到所有分组
          </Button>
        </Space>
        {duplicateKeys.length ? (
          <span className="text-xs text-rose-600">
            重复的分组 Key：{duplicateKeys.join('、')}（保存后只有最后一个生效）
          </span>
        ) : null}
      </div>
      <Table
        rowKey="id"
        size="small"
        border={false}
        columns={columns}
        data={rows}
        pagination={false}
        noDataElement={<Empty description="暂无分组，点击右上角添加" />}
      />
    </div>
  )
}
